/** The record page's Calculation tab: how one figure was made, from the receipt - ADR-0212, rule 3.
 *
 * Four cards: the sum (usage times factor is exact kilograms, which is rounded once to tonnes), a
 * grid of *where each number came from* with a link to open each, the market-based sum when the
 * record has one, and the trail of chips a number goes through (bill stored, usage read, factor
 * chosen, calculated, verified, ledger).
 *
 * **This is a view of the receipt and never a source.** Every number is read from
 * `GET /receipts/{id}` (`useReceipt`) and the arithmetic is said by `calculationModel.ts`, which
 * follows the receipt drawer's own care; the drawer stays rule 3's single full rendering - it also
 * carries the licence attribution, the GWP set's references and the ledger hashes - and the button
 * on each card opens it. Two records of the same figure on one screen cannot disagree because the
 * second is built from the first's fields.
 *
 * Scope 2 can have two figures for one record, one per method. They are two cards and are **never
 * added together**; the location-based one comes first and a receipt always names its method.
 */

import { CheckCircle, Clock } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { Activity, Receipt } from "../api/queries";
import { useReceipt } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import type { GridColumn } from "../components/ui";
import { BasisChip, GridTable, InfoPopover, Skeleton, StatusChip } from "../components/ui";
import { statedQuantity, unitLabel } from "../lib/format";
import { calculationOf, exactKgOf, kgUnitOf, trailOf } from "./calculationModel";
import { SourceValue } from "./recordSource";
import type { SourceFields } from "./useSourceFields";
import { keepSearch } from "./search";
import styles from "./scopeTable/scopeTable.module.css";

type Calc = Activity["calculations"][number];

interface Row {
  key: string;
  number: string;
  /** Text, or - for a value read from the bill - a link that shows it there. */
  value: ReactNode;
  source: string;
  open: ReactNode;
}

function words(value: string): string {
  return value.replace(/_/g, " ");
}

/** Location-based (or the only basis) first; market-based after. */
function ordered(calculations: readonly Calc[]): Calc[] {
  const rank = (calc: Calc) => (calc.method === "market" ? 1 : 0);
  return [...calculations].sort((a, b) => rank(a) - rank(b));
}

function Sum({ receipt }: { receipt: Receipt }) {
  const sum = calculationOf(receipt);
  return (
    <>
      <div className={styles.sum} data-testid={`calc-sum-${receipt.calculation_id}`}>
        {sum.operands.map((operand, at) => (
          <span key={operand.key} style={{ display: "contents" }}>
            {at > 0 ? (
              <span className={styles.op} aria-hidden="true">
                {sum.operator}
              </span>
            ) : null}
            <div className={styles.box}>
              <small>{operand.label}</small>
              <b>{operand.value}</b>
              <span>{operand.unit}</span>
            </div>
          </span>
        ))}
        {sum.operands.length > 0 ? (
          <span className={styles.op} aria-hidden="true">
            =
          </span>
        ) : null}
        <div className={styles.box}>
          <small>Exact</small>
          <b>{sum.exactKg}</b>
          <span>{sum.kgUnit}</span>
        </div>
        <span className={styles.op} aria-hidden="true">
          →
        </span>
        <div className={styles.box} data-result="true">
          <small>Rounded</small>
          <b>{sum.roundedT}</b>
          <span>tCO₂e</span>
        </div>
      </div>
      {sum.note ? <p className={styles.tableNote}>{sum.note}</p> : null}
    </>
  );
}

function sourcesOf(receipt: Receipt, onTab: (tab: "document") => void, source: SourceFields): Row[] {
  const sum = calculationOf(receipt);
  const rows: Row[] = [];
  const document = receipt.source_document;
  const attested = receipt.reference_kind === "stated_basis";
  rows.push({
    key: "usage",
    number: receipt.category === "fugitive" ? "Refrigerant" : "Usage",
    // A value read from the bill is a link to the place it was read from (P15 item 1); one that is
    // not, because nothing confirmed its position or the figure has no document, stays text.
    value: (
      <SourceValue source={source} of="quantity">
        {`${statedQuantity(receipt.activity_quantity)} ${unitLabel(receipt.activity_unit)}`}
      </SourceValue>
    ),
    source: attested
      ? "Typed in by hand, on a stated basis"
      : document
        ? `${document.original_filename ?? "Untitled document"}`
        : "No document",
    open:
      attested || document ? (
        <button type="button" className={styles.figureButton} onClick={() => onTab("document")}>
          {attested ? "Open basis" : "Open bill"}
        </button>
      ) : (
        "—"
      ),
  });
  if (receipt.factor.value !== null) {
    const publisher = [receipt.factor.publisher, receipt.factor.edition].filter(Boolean).join(", ");
    const factor = sum.operands.find((operand) => operand.key === "factor");
    rows.push({
      key: "factor",
      number: "Factor",
      value: factor ? `${factor.value} ${factor.unit}` : receipt.factor.value,
      source: publisher || (receipt.factor.gwp_set_label ? `GWP set ${receipt.factor.gwp_set_label}` : "—"),
      open: receipt.factor.factor_id ? (
        <Link
          to="/factors/$factorId"
          params={{ factorId: receipt.factor.factor_id }}
          search={keepSearch}
          className={styles.figureButton}
        >
          Open factor
        </Link>
      ) : (
        "—"
      ),
    });
  }
  rows.push({
    key: "exact",
    number: "Exact result",
    value: `${sum.exactKg} ${kgUnitOf(receipt)}`,
    source: sum.operands.length > 0 ? sum.operands.map((operand) => operand.value).join(` ${sum.operator} `) : "As the engine stated it",
    open: "—",
  });
  rows.push({
    key: "rounded",
    number: "Rounded result",
    value: `${sum.roundedT} tCO₂e`,
    source: `${exactKgOf(receipt.figure_kgco2e)} kg ÷ 1,000, rounded to 2 decimals`,
    open: "—",
  });
  for (const entry of receipt.ledger) {
    rows.push({
      key: `ledger-${String(entry.seq)}`,
      number: "Ledger entry",
      value: `#${String(entry.seq)}`,
      source: words(entry.type),
      open: (
        <Link
          to="/ledger/$seq"
          params={{ seq: String(entry.seq) }}
          search={keepSearch}
          className={styles.figureButton}
        >
          Open entry
        </Link>
      ),
    });
  }
  return rows;
}

const COLUMNS: GridColumn<Row>[] = [
  { id: "number", header: "Number", rowHeader: true, cell: (row) => row.number },
  { id: "value", header: "Value", numeric: true, cell: (row) => row.value },
  { id: "source", header: "Source", cell: (row) => row.source },
  { id: "open", header: "Open", cell: (row) => row.open },
];

function Card({
  title,
  info,
  action,
  children,
}: {
  title: string;
  info?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="card">
      <h2 className={styles.cardHead}>
        {title}
        {info}
        <span className={styles.sp} />
        {action}
      </h2>
      {children}
    </section>
  );
}

export function RecordCalculation({
  activity,
  onReceipt,
  onTab,
  source,
}: {
  activity: Activity;
  onReceipt: (calculationId: string) => void;
  onTab: (tab: "document") => void;
  source: SourceFields;
}) {
  const calcs = ordered(activity.calculations);
  const primary = calcs[0];
  const market = calcs.find((calc) => calc.method === "market" && calc !== primary);
  const primaryRead = useReceipt(primary?.calculation_id);
  const marketRead = useReceipt(market?.calculation_id);

  if (!primary) {
    return (
      <Card title="How this figure is calculated">
        <p className={styles.tableNote} data-testid="calc-none">
          Nothing has been priced from this record yet.
        </p>
      </Card>
    );
  }

  if (primaryRead.error) {
    return (
      <ReadFailed of="This figure's receipt" error={primaryRead.error} onRetry={() => void primaryRead.refetch()} />
    );
  }
  const receipt = primaryRead.data;
  if (!receipt) {
    return (
      <Card title="How this figure is calculated">
        <div className={styles.cardBody} role="status">
          <span className="visuallyHidden">Loading…</span>
          <Skeleton height={64} />
        </div>
      </Card>
    );
  }

  const open = (id: string) => (
    <button type="button" className={styles.ghost} onClick={() => onReceipt(id)}>
      Open receipt
    </button>
  );

  return (
    <>
      <Card
        title={receipt.method ? `How this figure is calculated, ${receipt.method}-based` : "How this figure is calculated"}
        info={
          <>
            <InfoPopover infoKey="record.calculation" />
            <BasisChip basis={activity.basis} />
          </>
        }
        action={open(receipt.calculation_id)}
      >
        <Sum receipt={receipt} />
      </Card>

      <Card title="Where each number came from">
        <GridTable
          caption="Where each number in this figure came from"
          columns={COLUMNS}
          rows={sourcesOf(receipt, onTab, source)}
          rowKey={(row) => row.key}
          rowNumbers={false}
          zebra={false}
          testId="calc-sources"
        />
      </Card>

      {market ? (
        <Card
          title="Market-based"
          info={<InfoPopover infoKey="scope2.market-based" />}
          action={open(market.calculation_id)}
        >
          {marketRead.error ? (
            <ReadFailed
              of="The market-based receipt"
              error={marketRead.error}
              onRetry={() => void marketRead.refetch()}
            />
          ) : marketRead.data ? (
            <Sum receipt={marketRead.data} />
          ) : (
            <div className={styles.cardBody} role="status">
              <span className="visuallyHidden">Loading…</span>
              <Skeleton height={64} />
            </div>
          )}
        </Card>
      ) : null}

      <Card title="Trail">
        <ul className={styles.trail} aria-label="The trail of this figure" data-testid="calc-trail">
          {trailOf(receipt).map((link) => (
            <li key={link.key} data-met={link.met}>
              <StatusChip tone={link.met ? "ok" : "neutral"} icon={link.met ? CheckCircle : Clock}>
                {link.label}
              </StatusChip>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
